import { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { isMobileOnly } from 'react-device-detect';
import { withTheme, useTheme } from 'styled-components';
import type { ThemeProps, DefaultTheme } from 'styled-components';

/* Child Components */
import Button, { BUTTON_SIZE_KEYS } from 'components/common/Button';
import VideoModal from 'components/common/VideoModal';
import WishLocalLogo from 'components/common/WishLocalLogo';
import HelpModal from 'components/landing/HelpModal';
import CollapsibleFAQ from 'components/landing/CollapsibleFAQ';
import Footer from 'components/landing/Footer';
import CloseIcon from 'icons/CloseIcon';
import PhoneIcon from 'icons/PhoneIcon';

/* Styled Components */
import {
  Wrapper,
  Header,
  RightHeader,
  LinksContainer,
  ScrollLink,
  HeroImage,
  HeroImageContentContainer,
  HeroImageTitle,
  HeroImageSubtitle,
  HeroImageButtonsContainer,
  HeroImageCTAContainer,
  HeroImageCTAButtonWrapper,
  HeroImageButtonText,
  HeroImageWatchDemoButton,
  Section,
  SectionTitle,
  SectionBody,
  ColumnContainer,
  ImageContainer,
  SectionSubTitle,
  BodyText,
  VideoSection,
  VideoTextContainer,
  VideoThumbnailContainer,
  VideoThumbnailText,
  VideoThumbnailTitle,
  RowContainer,
  TestimonialsContainer,
  TestimonialsWrapper,
  TestimonialCard,
  BoldText,
  AboutWishSection,
  AboutWishStats,
  WishAppImageWrapper,
  SignupButtonContainer,
  SignupBonus,
  SignupIncentiveCaption,
  CallWrapper,
  CallTooltip,
  CallTextWrapper,
  CallText,
  CloseIconWrapper,
  PhoneIconWrapper,
  AnimationContainer,
} from 'styles/landing/LandingPage';

/* Icons */
import PlayIcon from 'icons/PlayIcon';
import SmallMoneyBagIcon from 'icons/SmallMoneyBagIcon';

/* Assets */
import StoreIllustration from 'assets/StoreIllustration.png';
import WishAppScreenshot from 'assets/WishAppScreenshot.png';

/* Lottie Animations */
import MoneyAnimation from 'lotties/money.json';
import HouseAnimation from 'lotties/house.json';
import TrophyAnimation from 'lotties/trophy.json';

/* Selectors */
import { getOverviewVideoURL } from 'app/appSlice';
import { getEmail } from 'app/signupSlice';
import { getSignupBonusFormattedAmount } from 'app/marketingSlice';
import { getShowExperiment } from 'app/experimentSlice';

/* Constants */
import { BLUE_VISITOR_UUID } from 'constants/AppConstants';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_EVENT_TYPE,
} from 'constants/AnalyticsConstants';
import { SIGNUP_PAGE_ROUTE } from 'constants/RouteConstants';
import {
  HOW_IT_WORKS_DATA,
  TESTIMONIAL_DATA,
  ABOUT_WISH_DATA,
} from 'constants/LandingPageConstants';
import { LANDING_PAGE_SHOW_CALL } from 'constants/ExperimentConstants';

/* Utils */
import { i18n } from 'utils/i18n';
import { getSessionStorage } from 'utils/SessionStorage';
import smoothScroll from 'utils/SmoothScroll';
import useWindowDimensions from 'utils/WindowDimensions';
import { fbLead } from 'utils/Facebook';
import {
  sendGAEvent,
  GA_GROUP_NAME,
  OLD_BLUE_SIGNUP_CONVERSION,
  EVENT_CATEGORY,
  EVENT_ACTION,
  SIGNUP_BUTTON_LABEL,
  VIDEO_LABEL,
  BUTTON_LABEL,
} from 'utils/GoogleAnalytics';
import useModal from 'utils/UseModal';
import { loadAnimation } from 'utils/Lottie';

/* Types */
import type { AppState } from 'app/store';

/* Services */
import AnalyticsService from 'services/AnalyticsService';

const mapStateToProps = (state: AppState) => ({
  overviewVideoURL: getOverviewVideoURL(state),
  newLandingPageEmail: getEmail(state),
  signupBonusFormattedAmount: getSignupBonusFormattedAmount(state),
  shouldShowCallFeature: getShowExperiment(state, LANDING_PAGE_SHOW_CALL),
});

type PropsType = ReturnType<typeof mapStateToProps> & ThemeProps<DefaultTheme>;

const SECTION_TOP_BUFFER = 80;

const SECTIONS = {
  HERO_IMAGE: 'HERO_IMAGE',
  HOW_IT_WORKS: 'HOW_IT_WORKS',
  WHY_WISH_LOCAL: 'WHY_WISH_LOCAL',
  TESTIMONIALS: 'TESTIMONIALS',
  ABOUT_WISH: 'ABOUT_WISH',
  FAQ: 'FAQ',
} as const;

const onClickSection = (ref: React.MutableRefObject<HTMLDivElement | null>) => {
  const top = ref.current?.getBoundingClientRect().top;
  if (top !== undefined && top !== null) {
    const newScrollY = Math.floor(window.scrollY + top - SECTION_TOP_BUFFER);
    smoothScroll(window, newScrollY);
  }
};

const LandingPage = ({
  overviewVideoURL,
  newLandingPageEmail,
  signupBonusFormattedAmount,
  shouldShowCallFeature,
}: PropsType) => {
  const { height: screenHeight } = useWindowDimensions();
  const theme = useTheme();
  const [currentSection, setCurrentSection] = useState<Values<typeof SECTIONS>>(
    SECTIONS.HERO_IMAGE,
  );
  const [playVideo, setPlayVideo] = useState<boolean>(false);
  const [showCallIcon, setShowCallIcon] = useState<boolean>(false);
  const [showCallTooltip, setShowCallTooltip] = useState<boolean>(true);
  const [isHelpModalShown, openHelpModal, closeHelpModal] = useModal();
  const heroImageRef = useRef<HTMLDivElement | null>(null);
  const howItWorkSectionsRef = useRef<HTMLDivElement | null>(null);
  const whyWishLocalSectionRef = useRef<HTMLDivElement | null>(null);
  const testimonialsSectionRef = useRef<HTMLDivElement | null>(null);
  const aboutWishSectionRef = useRef<HTMLDivElement | null>(null);
  const faqSectionRef = useRef<HTMLDivElement | null>(null);

  const moneyAnimationRef = useRef<HTMLDivElement | null>(null);
  const houseAnimationRef = useRef<HTMLDivElement | null>(null);
  const trophyAnimationRef = useRef<HTMLDivElement | null>(null);

  const tabsData = [
    {
      section: SECTIONS.HOW_IT_WORKS,
      text: i18n('How it works'),
      onClick: () => onClickSection(howItWorkSectionsRef),
    },
    {
      section: SECTIONS.WHY_WISH_LOCAL,
      text: i18n('Why Wish Local'),
      onClick: () => onClickSection(whyWishLocalSectionRef),
    },
    {
      section: SECTIONS.TESTIMONIALS,
      text: i18n('Testimonials'),
      onClick: () => onClickSection(testimonialsSectionRef),
    },
    {
      section: SECTIONS.ABOUT_WISH,
      text: i18n('About Wish'),
      onClick: () => onClickSection(aboutWishSectionRef),
    },
    {
      section: SECTIONS.FAQ,
      text: i18n('FAQs'),
      onClick: () => onClickSection(faqSectionRef),
    },
  ] as const;

  const sections = [
    { ref: heroImageRef, section: SECTIONS.HERO_IMAGE },
    { ref: howItWorkSectionsRef, section: SECTIONS.HOW_IT_WORKS },
    { ref: whyWishLocalSectionRef, section: SECTIONS.WHY_WISH_LOCAL },
    { ref: testimonialsSectionRef, section: SECTIONS.TESTIMONIALS },
    { ref: aboutWishSectionRef, section: SECTIONS.ABOUT_WISH },
    { ref: faqSectionRef, section: SECTIONS.FAQ },
  ] as const;

  const onScroll = () => {
    for (const { ref, section } of sections) {
      if (ref.current) {
        const { top } = ref.current.getBoundingClientRect();
        if (top > 0 && top < screenHeight * 0.6) {
          if (section !== currentSection) {
            setCurrentSection(section);
            sendGAEvent(GA_GROUP_NAME, EVENT_ACTION.PAGE_SCROLL, {
              event_category: EVENT_CATEGORY.LANDING_PAGE,
              event_label: section.toLowerCase(),
            });
          }
          break;
        }
      }
    }
  };

  const onClickCallIcon = () => {
    openHelpModal();
    sendGAEvent(GA_GROUP_NAME, EVENT_ACTION.BUTTON_CLICK, {
      event_category: EVENT_CATEGORY.LANDING_PAGE,
      event_label: BUTTON_LABEL.CALL_ICON,
    });
  };

  useEffect(() => {
    AnalyticsService({
      event_type: ANALYTICS_EVENT_TYPE.WISH_LOCAL_WEB_EVENTS,
      event: ANALYTICS_EVENTS.LANDING_PAGE_VISIT,
      visitor_uuid: getSessionStorage(BLUE_VISITOR_UUID) || '',
      page_version: 'microservice',
    });
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  });

  useEffect(() => {
    if (!showCallIcon && currentSection !== SECTIONS.HERO_IMAGE) {
      setShowCallIcon(true);
    }
  }, [currentSection, showCallIcon]);

  useEffect(() => {
    if (moneyAnimationRef.current) {
      loadAnimation(MoneyAnimation, moneyAnimationRef.current);
    }
    if (houseAnimationRef.current) {
      loadAnimation(HouseAnimation, houseAnimationRef.current);
    }
    if (trophyAnimationRef.current) {
      loadAnimation(TrophyAnimation, trophyAnimationRef.current);
    }
  }, []);

  const onClickSignUp = (buttonLabel?: string) => {
    sendGAEvent(OLD_BLUE_SIGNUP_CONVERSION, EVENT_ACTION.CONVERSION);
    fbLead();
    AnalyticsService({
      event_type: ANALYTICS_EVENT_TYPE.WISH_LOCAL_WEB_EVENTS,
      event: ANALYTICS_EVENTS.LANDING_PAGE_CLICK_SIGNUP,
      visitor_uuid: getSessionStorage(BLUE_VISITOR_UUID) || '',
      page_version: 'microservice',
      landing_page_email: newLandingPageEmail,
    });
    sendGAEvent(GA_GROUP_NAME, EVENT_ACTION.BUTTON_CLICK, {
      event_category: EVENT_CATEGORY.LANDING_PAGE,
      event_label: buttonLabel,
    });
  };

  return (
    <Wrapper>
      {playVideo && (
        <VideoModal
          url={overviewVideoURL}
          onClose={() => setPlayVideo(false)}
        />
      )}
      <Header isFixed>
        <WishLocalLogo width={isMobileOnly ? '121' : '168'} />
        <RightHeader>
          <LinksContainer>
            {tabsData.map(({ section, text, onClick }) => (
              <ScrollLink
                key={text}
                isSelected={currentSection === section}
                onClick={onClick}
              >
                {text}
              </ScrollLink>
            ))}
          </LinksContainer>
          <Button
            bold
            size={BUTTON_SIZE_KEYS.MEDIUM}
            to={SIGNUP_PAGE_ROUTE}
            onClick={() => onClickSignUp(SIGNUP_BUTTON_LABEL.HEADER)}
          >
            {i18n('Sign up for free')}
          </Button>
        </RightHeader>
      </Header>
      <HeroImage ref={heroImageRef}>
        <HeroImageContentContainer>
          <HeroImageTitle>
            {i18n('Bring more customers into your store')}
          </HeroImageTitle>
          <HeroImageSubtitle>
            {i18n(
              '{%1=number of Wish shoppers} people shop on Wish every month. Add your store to our platform to expand your reach and meet new customers.',
              '44M+',
            )}
          </HeroImageSubtitle>
          <HeroImageButtonsContainer>
            <HeroImageWatchDemoButton
              onClick={() => {
                setPlayVideo(true);
                sendGAEvent(GA_GROUP_NAME, EVENT_ACTION.VIDEO_PLAY, {
                  event_category: EVENT_CATEGORY.LANDING_PAGE,
                  event_label: VIDEO_LABEL.HERO_IMAGE,
                });
              }}
            >
              <PlayIcon
                inverted
                overrideStyle={{ height: 24, width: 24, marginRight: 8 }}
              />
              <HeroImageButtonText>
                {i18n('Watch the demo')}
              </HeroImageButtonText>
            </HeroImageWatchDemoButton>
            <HeroImageCTAContainer>
              {signupBonusFormattedAmount ? (
                <SignupButtonContainer>
                  <SignupBonus>
                    <SmallMoneyBagIcon
                      overrideStyle={{ width: 94, marginRight: 8 }}
                    />
                    <SignupIncentiveCaption>
                      {i18n(
                        'Get {%1=bonus amount in local currency} when you sign up for Wish Local.',
                        signupBonusFormattedAmount,
                      )}
                    </SignupIncentiveCaption>
                  </SignupBonus>
                  <Button
                    bold
                    stretch
                    size={BUTTON_SIZE_KEYS.LARGE}
                    overrideStyle={{
                      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                    }}
                    to={SIGNUP_PAGE_ROUTE}
                    onClick={() =>
                      onClickSignUp(SIGNUP_BUTTON_LABEL.HERO_IMAGE)
                    }
                  >
                    {i18n('Sign up for free')}
                  </Button>
                </SignupButtonContainer>
              ) : (
                <HeroImageCTAButtonWrapper>
                  <Button
                    bold
                    stretch
                    size={BUTTON_SIZE_KEYS.LARGE}
                    overrideStyle={{
                      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
                    }}
                    to={SIGNUP_PAGE_ROUTE}
                    onClick={() =>
                      onClickSignUp(SIGNUP_BUTTON_LABEL.HERO_IMAGE)
                    }
                  >
                    {i18n('Sign up for free')}
                  </Button>
                </HeroImageCTAButtonWrapper>
              )}
            </HeroImageCTAContainer>
          </HeroImageButtonsContainer>
        </HeroImageContentContainer>
      </HeroImage>
      <Section ref={howItWorkSectionsRef}>
        <SectionTitle margin="0 0 24px 0">
          {i18n('How Wish Local works for your store')}
        </SectionTitle>
        <SectionBody>
          {HOW_IT_WORKS_DATA().map(({ imageSrc, subTitle, bodyText }) => (
            <ColumnContainer key={subTitle}>
              <ImageContainer roundCorner src={imageSrc} />
              <SectionSubTitle bold margin="8px 0">
                {subTitle}
              </SectionSubTitle>
              <BodyText>{bodyText}</BodyText>
            </ColumnContainer>
          ))}
        </SectionBody>
      </Section>
      <VideoSection ref={whyWishLocalSectionRef}>
        <VideoThumbnailContainer
          onClick={() => {
            setPlayVideo(true);
            sendGAEvent(GA_GROUP_NAME, EVENT_ACTION.VIDEO_PLAY, {
              event_category: EVENT_CATEGORY.LANDING_PAGE,
              event_label: VIDEO_LABEL.OVERVIEW,
            });
          }}
        >
          <RowContainer>
            <ImageContainer src={StoreIllustration} />
          </RowContainer>
          <VideoThumbnailTitle>
            <WishLocalLogo width="100%" />
            <VideoThumbnailText>{i18n('Program Overview')}</VideoThumbnailText>
          </VideoThumbnailTitle>
          <PlayIcon
            overrideStyle={{
              height: 64,
              width: 64,
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: 0,
              right: 0,
              margin: 'auto',
            }}
          />
        </VideoThumbnailContainer>
        <VideoTextContainer>
          <SectionTitle color={theme.white}>
            {i18n('Why Wish Local')}
          </SectionTitle>
          <SectionSubTitle color={theme.white} margin="16px 0">
            {i18n(
              'Enjoy features designed to help your business succeed and grow with Wish Local',
            )}
          </SectionSubTitle>
          <ColumnContainer
            style={{ margin: 0, justifyContent: 'space-evenly' }}
          >
            <RowContainer>
              <AnimationContainer ref={moneyAnimationRef} />
              <BodyText margin="12px 0" color={theme.white}>
                {i18n(
                  'Earn a commission on every eligible pickup and upsell your products in-store',
                )}
              </BodyText>
            </RowContainer>
            <RowContainer>
              <AnimationContainer ref={houseAnimationRef} />
              <BodyText margin="12px 0" color={theme.white}>
                {i18n(
                  'Keep track of Wish packages with convenient inventory management tools',
                )}
              </BodyText>
            </RowContainer>
            <RowContainer>
              <AnimationContainer ref={trophyAnimationRef} />
              <BodyText margin="12px 0" color={theme.white}>
                {i18n(
                  "Best of all, participating in Wish Local is free. It's a win-win!",
                )}
              </BodyText>
            </RowContainer>
          </ColumnContainer>
        </VideoTextContainer>
      </VideoSection>
      <Section ref={testimonialsSectionRef} style={{ paddingBottom: 0 }}>
        <SectionTitle>
          {i18n('See how stores thrive with Wish Local')}
        </SectionTitle>
        <SectionSubTitle margin="16px 0">
          {i18n(
            "Don't just take our word for it! Take a look at our participant stories, and see how Wish Local has made a difference for them.",
          )}
        </SectionSubTitle>
      </Section>
      <TestimonialsContainer>
        <TestimonialsWrapper>
          {TESTIMONIAL_DATA().map(
            ({ title, storeName, location, testimonial, imageSrc }) => (
              <TestimonialCard key={title}>
                <ImageContainer src={imageSrc} />
                <ColumnContainer
                  style={{ padding: '8px 20px 24px', margin: 0 }}
                >
                  <BoldText margin="8px 0">{title}</BoldText>
                  <BodyText color={theme.Grey3}>
                    {`${storeName} · ${location}`}
                  </BodyText>
                  <BodyText margin="8px 0">&quot;{testimonial}&quot;</BodyText>
                </ColumnContainer>
              </TestimonialCard>
            ),
          )}
        </TestimonialsWrapper>
      </TestimonialsContainer>
      <Section backgroundColor={theme.Blue1} ref={aboutWishSectionRef}>
        <AboutWishSection>
          <WishAppImageWrapper>
            <ImageContainer src={WishAppScreenshot} />
          </WishAppImageWrapper>
          <ColumnContainer>
            <SectionTitle color={theme.white}>
              {i18n('Wish is one of the most downloaded shopping apps')}
            </SectionTitle>
            <SectionSubTitle color={theme.white} margin="16px 0">
              {i18n(
                "With Wish's growing platform by your side, your store will attract loyal Wish customers and reach a world of new shoppers",
              )}
            </SectionSubTitle>
            <AboutWishStats>
              {ABOUT_WISH_DATA(theme).map((text, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <BodyText color={theme.white} margin="8px 0" key={index}>
                  {text}
                </BodyText>
              ))}
            </AboutWishStats>
          </ColumnContainer>
        </AboutWishSection>
      </Section>
      <Section ref={faqSectionRef}>
        <CollapsibleFAQ />
      </Section>
      <Footer onClickSignUp={() => onClickSignUp(SIGNUP_BUTTON_LABEL.FOOTER)} />
      {shouldShowCallFeature && (
        <>
          {showCallIcon && (
            <CallWrapper>
              {showCallTooltip && (
                <CallTooltip>
                  <CallTextWrapper>
                    <CallText>{i18n('Questions?')}</CallText>
                    <CallText>{i18n('Request a call')}</CallText>
                  </CallTextWrapper>
                  <CloseIconWrapper onClick={() => setShowCallTooltip(false)}>
                    <CloseIcon overrideStyle={{ color: theme.white }} />
                  </CloseIconWrapper>
                </CallTooltip>
              )}
              <PhoneIconWrapper onClick={onClickCallIcon}>
                <PhoneIcon />
              </PhoneIconWrapper>
            </CallWrapper>
          )}
          {isHelpModalShown && <HelpModal onClose={closeHelpModal} />}
        </>
      )}
    </Wrapper>
  );
};

export default connect(mapStateToProps)(withTheme(LandingPage));
